/**
 * editor-state-logic.ts — pure selection-state computation extracted from editor-state.ts.
 *
 * Epic: docs/epics/active/ts-unit-testability-seams-260623/main.md
 * Phase 2 (open editor seam front — editor-state logic seam).
 *
 * This module contains the pure selection-computation logic from editor-state.ts.
 * It has NO DOM dependencies (no window/document/localStorage) — safe to
 * import in any environment including Node.js vitest without a DOM shim.
 *
 * What is here:
 *   - `SelectionState` — typed shape for the two sync'd selection fields.
 *   - `computeSelectionState(idOrSet)` — pure function; no DOM; no side effects.
 *     Extracted from `setEditorSelection` in editor-state.ts so the selection
 *     algorithm is independently testable.
 *
 * What stays in editor-state.ts:
 *   - The `state` singleton (requires DOM via `generateSessionId`, `OpLog`, etc.)
 *   - The `els` singleton (requires DOM via `getRequired`/`querySelectorRequired`)
 *   - The `_bboxCache` singleton (requires `state.interop`)
 *   - `setEditorSelection` (delegates to `computeSelectionState` + applies to state)
 *
 * No DOM globals accessed at module scope — import-safe in any environment.
 */
// ─── computeSelectionState ──────────────────────────────────────────────────────
/**
 * Compute the `{selectedStructId, selectedStructIds}` fields from a selection input.
 *
 * Pure function — no DOM, no side effects. The caller (setEditorSelection in
 * editor-state.ts) applies the result to the module-scope `state` object.
 *
 * idOrSet may be:
 *   - a string structId          → single-select: both fields sync'd to that id
 *   - a Set<string>              → multi-select: selectedStructId null if size > 1
 *   - an Iterable<string>        → coerced to Set, same rules as Set above
 *   - null / undefined           → clear: both fields reset to empty
 *   - empty string               → treated as string; selectedStructId falsy-coerces to null,
 *                                  selectedStructIds becomes a singleton containing ""
 */
export function computeSelectionState(idOrSet) {
    if (idOrSet == null) {
        return {
            selectedStructIds: new Set(),
            selectedStructId: null,
        };
    }
    else if (typeof idOrSet === "string") {
        return {
            selectedStructIds: new Set([idOrSet]),
            // WHY `|| null`: empty string is falsy in JS — same as "no selection" from
            // the caller's perspective. Mirrors the original setEditorSelection behaviour.
            selectedStructId: idOrSet || null,
        };
    }
    else {
        // Treat as Set<string> or any Iterable<string>
        const s = idOrSet instanceof Set ? idOrSet : new Set(idOrSet);
        return {
            selectedStructIds: s,
            // WHY: s.size === 1 guarantees element at index 0 exists (non-undefined)
            selectedStructId: s.size === 1 ? Array.from(s)[0] : null,
        };
    }
}
